import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { RefreshCw, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { compensationAPI, carrierAPI } from '../../lib/api';
import { fmt } from './_shared';

// Weekly Cycle Dashboard tab — Thursday→Wednesday 23:59 ET cycle, Friday payday.
export function CycleTab() {
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    carrier_id: '', agency_id: '', manager_id: '', product_category: '', status: '',
  });
  const [carriers, setCarriers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { offset };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await compensationAPI.cycleDashboard(params);
      setData(r.data);
    } catch {
      toast.error('Failed to load cycle dashboard');
    } finally {
      setLoading(false);
    }
  }, [offset, filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    carrierAPI.list()
      .then((r) => setCarriers(Array.isArray(r.data) ? r.data : (r.data.carriers || [])))
      .catch(() => {});
  }, []);

  if (!data) {
    return (
      <Card><CardContent className="p-6"><div className="h-32 animate-pulse bg-zinc-100 rounded" /></CardContent></Card>
    );
  }

  const t = data.totals;
  const tiles = [
    { key: 'imported', label: 'Imported', amt: t.imported_amount, cnt: t.imported_count, color: 'border-blue-300' },
    { key: 'pending', label: 'Pending Review', amt: t.pending_review_amount, cnt: t.pending_review_count, color: 'border-amber-300' },
    { key: 'approved', label: 'Approved', amt: t.approved_amount, cnt: t.approved_count, color: 'border-emerald-300' },
    { key: 'scheduled', label: 'Scheduled', amt: t.scheduled_amount, cnt: t.scheduled_count, color: 'border-sky-300' },
    { key: 'paid', label: 'Paid', amt: t.paid_amount, cnt: t.paid_count, color: 'border-green-300' },
    { key: 'adjustments', label: 'Adjustments', amt: t.adjustments_total, cnt: t.adjustments_count, color: 'border-violet-300' },
    { key: 'chargebacks', label: 'Chargebacks', amt: t.chargebacks_total, cnt: t.chargebacks_count, color: 'border-red-300' },
    { key: 'estimated', label: 'Est. Friday Payout', amt: t.estimated_friday_payout, cnt: t.record_count, color: 'border-indigo-400 bg-indigo-50' },
  ];

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <Button size="sm" variant="outline" onClick={() => setOffset(offset - 1)} data-testid="cycle-prev">← Previous</Button>
          <Button size="sm" variant="outline" onClick={() => setOffset(0)} disabled={offset === 0} data-testid="cycle-current">Current</Button>
          <Button size="sm" variant="outline" onClick={() => setOffset(offset + 1)} data-testid="cycle-next">Next →</Button>
          <div className="ml-2 flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-zinc-500" />
            <span className="font-semibold" data-testid="cycle-label">{data.label}</span>
            <Badge className="bg-indigo-100 text-indigo-800" data-testid="cycle-payday">Payday {data.payout_date}</Badge>
          </div>
          <Button size="sm" variant="ghost" onClick={load} disabled={loading} className="ml-auto">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 grid gap-3 md:grid-cols-5">
          <div>
            <Label className="text-xs">Carrier</Label>
            <Select value={filters.carrier_id || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, carrier_id: v === 'all' ? '' : v }))}>
              <SelectTrigger data-testid="cycle-filter-carrier"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All carriers</SelectItem>
                {carriers.map((c) => <SelectItem key={c.carrier_id} value={c.carrier_id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Agency ID</Label>
            <Input value={filters.agency_id} onChange={(e) => setFilters((f) => ({ ...f, agency_id: e.target.value }))} placeholder="agency_id" data-testid="cycle-filter-agency" />
          </div>
          <div>
            <Label className="text-xs">Manager ID</Label>
            <Input value={filters.manager_id} onChange={(e) => setFilters((f) => ({ ...f, manager_id: e.target.value }))} placeholder="manager_id" data-testid="cycle-filter-manager" />
          </div>
          <div>
            <Label className="text-xs">Product Category</Label>
            <Select value={filters.product_category || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, product_category: v === 'all' ? '' : v }))}>
              <SelectTrigger data-testid="cycle-filter-product"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {['ACA', 'Medicare', 'Supplemental', 'Life', 'Final Expense', 'Other'].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={filters.status || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === 'all' ? '' : v }))}>
              <SelectTrigger data-testid="cycle-filter-status"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {['Imported', 'Pending_Review', 'Accounting_Reviewed', 'Approved', 'Scheduled', 'Paid'].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-3 md:grid-cols-4">
        {tiles.map((tl) => (
          <Card key={tl.key} className={tl.color}>
            <CardContent className="p-5">
              <div className="text-xs uppercase text-zinc-500 tracking-wide">{tl.label}</div>
              <div className="text-2xl font-semibold mt-1" data-testid={`cycle-tile-${tl.key}`}>{fmt(tl.amt)}</div>
              <div className="text-xs text-zinc-500 mt-1">{tl.cnt} record{tl.cnt === 1 ? '' : 's'}</div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
